import { useState, useEffect } from 'react'
import { AdSupplierUser } from '@magnetar/data-access'
import { AdSupplier, User, UserRole } from '@magnetar/admin-api-interfaces'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { adSupplierUsersService } from '@/services/ad_supplier_users_service'
import { userRoleService } from '@/services/user_role_service'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

interface SupplierUserRoleDialogProps {
  supplierUser: AdSupplierUser & { user: User; supplier: AdSupplier }
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function SupplierUserRoleDialog({
  supplierUser,
  open,
  onOpenChange,
  onSuccess,
}: SupplierUserRoleDialogProps) {
  const { token } = useAuth()
  const { id: supplierId } = useViewMode()
  const [roles, setRoles] = useState<UserRole[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string>(
    supplierUser.user_role_id?.toString() || 'none'
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // 載入供應商角色列表
  useEffect(() => {
    if (open && supplierId && token) {
      setIsLoading(true)
      userRoleService
        .getUserRoles(
          { page: 1, pageSize: 100, scopeType: 'supplier', supplierId: supplierId },
          token
        )
        .then((response) => {
          if (response.success && response.data) {
            setRoles(response.data.items || [])
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [open, supplierId, token])

  // 重置選擇的角色
  useEffect(() => {
    if (open) {
      setSelectedRoleId(supplierUser.user_role_id?.toString() || 'none')
    }
  }, [open, supplierUser.user_role_id])

  const handleSave = async () => {
    if (!token) return

    setIsSaving(true)
    try {
      const roleId = selectedRoleId === 'none' ? null : parseInt(selectedRoleId)

      const result = await adSupplierUsersService.updateAdSupplierUser(
        supplierUser.supplier_id,
        supplierUser.supplier_user_id,
        {
          supplier_id: supplierUser.supplier_id,
          user_id: supplierUser.user_id,
          user_role_id: roleId,
        },
        token
      )

      if (result.success) {
        toast.success('角色設定已更新')
        onSuccess()
        onOpenChange(false)
      } else {
        toast.error(result.error?.message || '更新失敗')
      }
    } catch {
      toast.error('更新角色失敗')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>角色設定</DialogTitle>
          <DialogDescription>
            為 {supplierUser.user?.user_name} ({supplierUser.user?.account}) 設定供應商角色
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label htmlFor="role">供應商角色</Label>
          {isLoading ? (
            <Skeleton className="h-10 w-full mt-2" />
          ) : (
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="選擇角色" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">無角色</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                    {role.role_name}
                    {role.role_description && (
                      <span className="text-muted-foreground ml-2">
                        - {role.role_description}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!isLoading && roles.length === 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              此供應商尚未建立角色，請先至「角色管理」建立角色。
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? '儲存中...' : '儲存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
